import { useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight, ShieldCheck, Check } from 'lucide-react';
import StepIdentidad from './steps/StepIdentidad';
import StepPosicionamiento from './steps/StepPosicionamiento';
import StepNivel from './steps/StepNivel';
import StepValidacion from './steps/StepValidacion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: 'Identidad', title: 'Identidad Profesional' },
  { num: 2, label: 'Posicionamiento', title: 'Posicionamiento Estratégico' },
  { num: 3, label: 'Nivel', title: 'Nivel y Disponibilidad' },
  { num: 4, label: 'Validación', title: 'Compromiso y confidencialidad' },
];

const initialFormData = {
  nombre: '',
  email: '',
  telefono: '',
  linkedin: '',
  cargoActual: '',
  empresaActual: '',
  experiencia: '',
  situacionProfesional: '',
  tipoAsesor: '',
  areasEspecializacion: [],
  sectoresExperiencia: [],
  decisionCompleja: '',
  nivel: '',
  disponibilidad: '',
  modalidad: '',
  zonaPresencial: [],
  idiomas: [],
  conflictos: '',
  referido: '',
  aceptaSuccessFee: false,
  aceptaValidacion: false,
};

export default function AdvisorWizard() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submissionId] = useState(() => crypto.randomUUID());
  const [slideDir, setSlideDir] = useState('right');

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validateStep = (stepIndex) => {
    const errs = {};
    if (stepIndex === 0) {
      if (!formData.nombre.trim()) errs.nombre = 'Campo obligatorio';
      if (!formData.email.trim()) errs.email = 'Campo obligatorio';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Email no válido';
      if (!formData.telefono.trim()) errs.telefono = 'Campo obligatorio';
      if (!formData.linkedin.trim()) errs.linkedin = 'Campo obligatorio';
      if (!formData.cargoActual.trim()) errs.cargoActual = 'Campo obligatorio';
      if (!formData.experiencia) errs.experiencia = 'Seleccione un rango';
      if (!formData.situacionProfesional) errs.situacionProfesional = 'Seleccione una opción';
    } else if (stepIndex === 1) {
      if (!formData.tipoAsesor) errs.tipoAsesor = 'Seleccione un tipo';
      if (formData.areasEspecializacion.length === 0) errs.areasEspecializacion = 'Seleccione al menos un área';
      if (formData.areasEspecializacion.length > 5) errs.areasEspecializacion = 'Máximo 5 especialidades';
      if (formData.sectoresExperiencia.length === 0) errs.sectoresExperiencia = 'Seleccione al menos un sector';
      if (formData.sectoresExperiencia.length > 3) errs.sectoresExperiencia = 'Máximo 3 sectores';
      if (!formData.decisionCompleja.trim()) errs.decisionCompleja = 'Campo obligatorio';
      else if (formData.decisionCompleja.trim().length < 20) errs.decisionCompleja = `Mínimo 20 caracteres (${formData.decisionCompleja.trim().length}/20)`;
    } else if (stepIndex === 2) {
      if (!formData.nivel) errs.nivel = 'Seleccione un nivel';
      if (!formData.disponibilidad) errs.disponibilidad = 'Seleccione disponibilidad';
      if (!formData.modalidad) errs.modalidad = 'Seleccione una modalidad';
      if ((formData.modalidad === 'presencial' || formData.modalidad === 'hibrido') && formData.zonaPresencial.length === 0) errs.zonaPresencial = 'Seleccione al menos una provincia';
      if (formData.idiomas.length === 0) errs.idiomas = 'Seleccione al menos un idioma';
    } else if (stepIndex === 3) {
      if (!formData.aceptaSuccessFee) errs.aceptaSuccessFee = 'Debe aceptar las condiciones';
      if (!formData.aceptaValidacion) errs.aceptaValidacion = 'Debe aceptar el proceso de validación';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setSlideDir('right');
    setStep((s) => Math.min(s + 1, 3));
  };

  const goPrev = () => {
    setSlideDir('left');
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    if (submitted) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: submissionId,
          form_type: 'advisor',
          payload: formData,
          source: 'conadvy_landing',
          page_path: window.location.pathname,
          submitted_at: new Date().toISOString(),
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setSubmitError(true);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('Error submitting advisor registro:', e);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-[660px] mx-auto px-6">
        <div
          data-testid="advisor-wizard-success"
          className="bg-white border border-[#E4E6EA] rounded-sm p-12 md:p-16 text-center"
        >
          <div className="w-20 h-20 rounded-full border-2 border-[#C58C40] flex items-center justify-center mx-auto mb-8">
            <Check size={32} className="text-[#C58C40]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-normal text-[#00163E] mb-4">
            Solicitud recibida
          </h2>
          <p className="text-[#00163E]/70 text-base leading-relaxed max-w-md mx-auto mb-3">
            Su perfil será evaluado por el equipo de Conadvy en un plazo máximo de 48 horas.
          </p>
          <p className="text-[#8A9BB1] text-sm">
            Solo los perfiles que superen el proceso de validación serán activados en la red.
          </p>
        </div>
      </div>
    );
  }

  const stepComponents = [
    <StepIdentidad data={formData} errors={errors} onChange={updateField} />,
    <StepPosicionamiento data={formData} errors={errors} onChange={updateField} />,
    <StepNivel data={formData} errors={errors} onChange={updateField} />,
    <StepValidacion data={formData} errors={errors} onChange={updateField} />,
  ];

  return (
    <div className="max-w-[660px] mx-auto px-6" data-testid="advisor-wizard">
      {/* Minimal progress bar */}
      <div className="mb-8">
        <div className="flex gap-2 mb-3">
          {STEPS.map((_, i) => (
            <div
              key={i}
              data-testid={`adv-progress-segment-${i}`}
              className={`flex-1 h-[2px] rounded-full transition-colors duration-500 ${
                i <= step ? 'bg-[#C58C40]' : 'bg-white/15'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-[#8A9BB1] text-xs tracking-wide">
          Paso {step + 1} de {STEPS.length} — {STEPS[step].title}
        </p>
      </div>

      {/* Card (light card inside dark page) */}
      <div className="bg-white border border-[#E4E6EA] rounded-sm overflow-hidden">
        {/* Step Header */}
        <div className="px-8 md:px-10 pt-8 md:pt-10 pb-6 border-b border-[#E4E6EA]">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase">
            Paso {step + 1} — {STEPS[step].label}
          </span>
          <h3
            data-testid="adv-wizard-step-title"
            className="text-xl md:text-2xl font-normal text-[#00163E] mt-2"
          >
            {STEPS[step].title}
          </h3>
        </div>

        {/* Step Content */}
        <div className="relative overflow-hidden">
          <div
            key={step}
            className={`px-8 md:px-10 py-8 md:py-10 ${
              slideDir === 'right' ? 'wizard-slide-from-right' : 'wizard-slide-from-left'
            }`}
          >
            {stepComponents[step]}
          </div>
        </div>

        {/* Navigation */}
        <div className="px-8 md:px-10 py-5 border-t border-[#E4E6EA] flex items-center justify-between">
          {step > 0 ? (
            <button
              onClick={goPrev}
              data-testid="adv-wizard-prev-btn"
              className="flex items-center gap-2 text-[#00163E]/50 hover:text-[#00163E] text-sm tracking-wide transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Anterior</span>
            </button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <button
              onClick={goNext}
              data-testid="adv-wizard-next-btn"
              className="flex items-center gap-2 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-8 py-3 text-sm tracking-[0.1em] uppercase"
            >
              <span>Siguiente</span>
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="adv-wizard-submit-btn"
              className="flex items-center gap-2 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-8 py-3 text-sm tracking-[0.1em] uppercase disabled:opacity-50"
            >
              <ShieldCheck size={16} />
              <span>{submitting ? 'Enviando...' : 'Solicitar Acceso a la Red'}</span>
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <p data-testid="adv-wizard-submit-error" className="text-center text-red-500 text-sm mt-4">
          Error al enviar la solicitud. Por favor, inténtelo de nuevo.
        </p>
      )}
    </div>
  );
}
